<template>
  <Transition name="fade">
    <Dialog
      v-if="priceBreakdownModal"
      :title="`Order #${order_ref.order_no ? order_ref.order_no : 'N/A'} ${order_ref.order_type_detail ? order_ref.order_type_detail : ''} Price Breakdown`"
      :showCloseButton="true"
      :showFooter="can('update-price-breakdown')"
      :showSaveButton="false"
      :showCancelButton="false"
      :custom-footer="true"
      :lg="true"
      :loading="loading"
      @closeEvent="closeEvent"
    >
      <template #header-right>
        <div class="flex items-center gap-2 ml-auto mr-5">
          <div class="flex items-center gap-2">
            <template v-if="statusObjects.length > 0">
              <span class="rounded-full p-2" :class="statusObjects[0].color">
                <component
                  :is="statusObjects[0].icon"
                  class="w-6 h-6"
                  :stroke-width="3"
                />
              </span>
              <span class="flex flex-col mr-12">
                <a

                  v-if="order_ref.stripe_invoice_status !== 'manual'"
                  :href="`${stripeDashboardUrl}/invoices/${statusObjects[0].status}`"

                  target="_blank"
                  class="capitalize mr-8 font-bold"
                >
                  {{ statusObjects[0].status ?? 'N/A' }}
                </a>
                <span v-else class="capitalize mr-8 font-bold">
                  {{ statusObjects[0].status ?? 'N/A' }}
                </span>
                <span class="font-light">{{ statusObjects[0].description }}</span>
              </span>
            </template>
           <template v-if="order_ref?.stripe_invoice_number">
              <span>Invoice No:</span>
              <span v-if="!editingInvoiceNo">
                {{ order_ref.stripe_invoice_number }}
                <button
                  class="ml-2 px-4 py-2 text-normal border border-[#219f84] rounded text-[#219f84] hover:bg-[#219f84] hover:text-white"
                  @click="() => startEditingInvoiceNo()"
                >
                  Edit Invoice Number
                </button>
              </span>
              <span v-else class="flex items-center gap-2">
                <input
                  v-model="invoiceNoInput"
                  type="text"
                  placeholder="Edit Invoice No"
                  class="px-2 py-2 border border-gray-300 rounded-md w-40"
                />
                <button
                  @click="saveInvoiceNumber"
                  :disabled="savingInvoice || !invoiceNoInput.trim()"
                  class="flex gap-x-2 px-3 py-2 items-center text-[#219f84] border border-[#219f84] rounded-md cursor-pointer hover:text-white hover:bg-[#219F84] duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-[#219f84] disabled:hover:bg-white relative"
                >

                  Save
                </button>
                <button
                  @click="editingInvoiceNo = false"
                  class="flex px-3 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-200"
                >
                  Cancel
                </button>
              </span>
            </template>
            <template v-else>
              <span>Invoice Status:</span>
              <span>Not invoiced</span>
              <span class="flex items-center gap-2">
                <input
                  v-model="invoiceNoInput"
                  type="text"
                  placeholder="Enter Invoice No"
                  class="px-2 py-2 border border-gray-300 rounded-md w-100"
                />
                <button
                  v-if="invoiceNoInput && invoiceNoInput.toString().trim().length > 0"
                  @click="saveInvoiceNumber"
                  :disabled="savingInvoice"
                  class="flex gap-x-2 px-3 py-2 items-center text-[#219f84] border border-[#219f84] rounded-md cursor-pointer hover:text-white hover:bg-[#219F84] duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-[#219f84] disabled:hover:bg-white relative"
                >
                  <span v-if="savingInvoice" class="inline-block w-4 h-4 border-2 border-white rounded-full border-r-transparent animate-spin mr-2"></span>
                  Save Invoice No
                </button>
              </span>
            </template>
          </div>
          <!-- if user has update-price-breakdown permission, then save the order item and create invoice, otherwise just create invoice -->
          <button
            v-if="can('create-stripe-invoice') && !order_ref.stripe_invoice_id"
            class="flex gap-x-2 px-3 py-2 items-center text-[#219f84] border border-[#219f84] rounded-md cursor-pointer hover:text-white hover:bg-[#219F84] duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-[#219f84] disabled:hover:bg-white relative"
            @click="
              can('update-price-breakdown') ? updateOrderPrices(true) : handleStripeSync()
            "
            :disabled="stripeLoading"
          >
            <div
              v-if="stripeLoading"
              class="absolute inset-0 bg-white/50 flex items-center justify-center z-[100]"
            >
              <span
                class="border-[4px] border-primary rounded-full w-10 h-10 animate-spin border-r-transparent"
              ></span>
            </div>
            {{
              order_ref.stripe_invoice_id
                ? 'Credit current Invoice and create new'
                : 'Create Invoice'
            }}
          </button>

          <Dropdown
            v-if="order_ref.stripe_invoice_info"
            triggerStyle="flex gap-x-2 px-3 py-2 items-center text-[#219f84] border border-[#219f84] rounded-md cursor-pointer hover:text-white hover:bg-[#219F84]"
            :close-on-outside-click="true"
            :close-on-select="true"
          >
            <template #trigger>
              <span class="flex gap-1 items-center justify-center"
                >Actions <ChevronDownIcon class="size-5"
              /></span>
            </template>
            <template #contents>
              <ul>
                <li>
                  <a
                    class="px-2 py-2 text-[#292D32] cursor-pointer duration-300 hover:bg-[#F4F4F4] flex items-center justify-between gap-2"
                    :href="order_ref.stripe_invoice_info.invoice_pdf"
                    target="_blank"
                  >
                    Download Invoice PDF
                  </a>
                </li>
              </ul>
            </template>
          </Dropdown>
        </div>
      </template>
      <template #modal-content>
        <template v-for="(orderItemRef, orderItemRefIndex) in order_items">
          <template
          v-for="(factory_product, factoryProductIndex) in orderItemRef.factory_products"
          :key="`order-item-${orderItemRefIndex}factory_product_${factoryProductIndex}`"
        >
          <div class="flex justify-between gap-10 mb-5">
            <div class="flex items-center gap-2 mt-2 h-max">
              <img
                :src="getFilePreview(factory_product)"
                alt="Sku preview"
                class="w-20 border-0 img-thumbnail img-fluid"
                v-img-fallback
              />
              <div class="flex flex-col">
                <input v-if="factory_product.is_custom_product"
                  :disabled="!can('update-price-breakdown')"
                  class="px-4 py-1 mr-2 border border-gray-300 rounded-md"
                  v-model="factory_product.new_product_name"
                />
                <input v-else
                  :disabled="!can('update-price-breakdown')"
                  class="px-4 py-1 mr-2 border border-gray-300 rounded-md"
                  v-model="factory_product.product_name"
                />
                <span class="text-black-50">SKU: {{ factory_product.sku_number }}</span>
                <button
                  v-if="!factory_product.id"
                  @click="removeFactoryProduct(orderItemRefIndex, factoryProductIndex)"
                  class="flex p-1 text-red-500 transition-all border border-red-500 rounded-md cursor-pointer gap-x-1 w-max hover:bg-red-500 hover:text-white"
                >
                  <trash-icon class="size-5" />
                  Delete
                </button>
              </div>
            </div>
            <div class="flex flex-1 gap-10 max-w-[70%]">
              <div class="flex-1">
                <table class="w-full products-table">
                  <thead v-if="factoryProductIndex === 0">
                    <tr class="text-center">
                      <th class="px-4 py-2 font-semibold text-left">Size</th>
                      <th class="px-4 py-2 font-semibold">Quantity</th>
                      <th class="px-4 py-2 font-semibold">Price</th>
                      <th class="px-4 py-2 font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      class="text-center"
                      v-for="(size, size_idx) in factory_product.prices.sizes"
                      :key="size_idx"
                    >
                      <td class="p-1 w-[25%] text-left">{{ size.size }}</td>
                      <td class="p-1 w-[25%]">{{ size.quantity }}</td>
                      <td class="p-1 w-[25%]">
                        <span
                          ><input
                            :disabled="!can('update-price-breakdown')"
                            type="number"
                            class="w-20 p-1 mr-2 border border-gray-300 rounded-md outline-none"
                            :value="size.final_net_price"
                            @change="
                              (event: Event) => {
                                const input = event.target as HTMLInputElement;
                                const number = formatNumber(input.value)
                                input.value = number
                                size.final_net_price = number
                                handlePriceUpdate(size, size.quantity);
                              }
                            "
                          />{{
                            order_ref.additional_fields?.currency?.net_price?.code ??
                            ''
                          }}</span
                        >
                      </td>
                      <td class="p-1 w-[25%] text-right">
                        {{ formatNumber(size.final_net_price_with_quantity) }}
                        <strong>{{
                          order_ref.additional_fields?.currency?.net_price?.code ?? ''
                        }}</strong>
                      </td>
                    </tr>
                    <tr v-if="factory_product.prices.total_addons > 0">
                      <td colspan="4">
                        <h1
                          class="p-1 mb-1 font-semibold text-center border text-md border-grey-300"
                        >
                          Addons
                        </h1>
                        <table class="w-full mt-4">
                          <tbody>
                            <!-- simple addons starts -->
                            <template
                              v-for="(addon, addon_idx) in factory_product.prices.addons"
                              :key="addon_idx"
                            >
                              <tr class="text-center">
                                <td class="p-1 w-[25%] text-left">
                                  {{ addon.addon_name }}
                                </td>
                                <td class="p-1 w-[25%]"></td>
                                <td class="p-1 w-[25%]">
                                  <input
                                    :disabled="!can('update-price-breakdown')"
                                    type="number"
                                    class="w-20 p-1 mr-2 border border-gray-300 rounded-md outline-none"
                                    v-model="addon.final_net_price"
                                    @change="
                                      () => {
                                        addon.final_net_price = addon.final_net_price
                                          ? formatNumber(addon.final_net_price)
                                          : 0;
                                        handlePriceUpdate(
                                          addon,
                                          factory_product.prices?.total_quantity
                                        );
                                      }
                                    "
                                  />
                                  {{
                                    order_ref.additional_fields?.currency?.net_price
                                      ?.code ?? ''
                                  }}
                                </td>
                                <td class="p-1 w-[25%] text-right">
                                  {{ formatNumber(addon.final_net_price_with_quantity) }}
                                  <strong>{{
                                    order_ref.additional_fields?.currency?.net_price
                                      ?.code ?? ''
                                  }}</strong>
                                </td>
                              </tr>
                            </template>
                            <!-- simple addons ends -->
                            <!-- ungrouped addons starts -->
                            <template
                              v-for="(ungrouped_addon, ungrouped_addon_idx) in factory_product
                                .prices.ungrouped_addons"
                              :key="`ungrouped_addon_${ungrouped_addon_idx}`"
                            >
                              <tr class="text-center">
                                <td class="p-1 w-[25%] text-left">
                                  {{ ungrouped_addon.addon_name }}
                                </td>
                                <td class="p-1 w-[25%]"></td>
                                <td class="p-1 w-[25%]">
                                  <input
                                    :disabled="!can('update-price-breakdown')"
                                    type="number"
                                    class="w-20 p-1 mr-2 border border-gray-300 rounded-md outline-none"
                                    v-model="ungrouped_addon.final_net_price"
                                    @change="
                                      () => {
                                        ungrouped_addon.final_net_price =
                                          ungrouped_addon.final_net_price
                                            ? parseFloat(ungrouped_addon.final_net_price)
                                            : 0;
                                        handlePriceUpdate(
                                          ungrouped_addon,
                                          factory_product.prices?.total_quantity
                                        );
                                      }
                                    "
                                  />
                                  {{
                                    order_ref.additional_fields?.currency?.net_price
                                      ?.code ?? ''
                                  }}
                                </td>
                                <td class="p-1 w-[25%] text-right">
                                  {{ formatNumber(ungrouped_addon.final_net_price_with_quantity) }}
                                  <strong>{{
                                    order_ref.additional_fields?.currency?.net_price
                                      ?.code ?? ''
                                  }}</strong>
                                </td>
                              </tr>
                            </template>
                            <!-- ungrouped addons ends -->
                            <!-- grouped addons starts -->
                            <template
                              v-for="(grouped_addon, group_name) in factory_product.prices
                                .grouped_addons"
                              :key="`grouped_${group_name}_selected_addon`"
                            >
                              <tr class="text-center">
                                <td class="p-1 w-[25%] text-left">
                                  {{ group_name }}:
                                  {{ grouped_addon.addon_name }}
                                </td>
                                <td class="p-1 w-[25%]"></td>
                                <td class="p-1 w-[25%]">
                                  <input
                                    :disabled="!can('update-price-breakdown')"
                                    type="number"
                                    class="w-20 p-1 mr-2 border border-gray-300 rounded-md outline-none"
                                    v-model="grouped_addon.final_net_price"
                                    @change="
                                      () => {
                                        grouped_addon.final_net_price =
                                          grouped_addon.final_net_price
                                            ? parseFloat(grouped_addon.final_net_price)
                                            : 0;
                                        handlePriceUpdate(
                                          grouped_addon,
                                          factory_product.prices?.total_quantity
                                        );
                                      }
                                    "
                                  />{{
                                    order_ref.additional_fields?.currency?.net_price
                                      ?.code ?? ''
                                  }}
                                </td>
                                <td class="p-1 w-[25%] text-right">
                                  {{ formatNumber(grouped_addon.final_net_price_with_quantity) }}
                                  <strong>{{
                                    order_ref.additional_fields?.currency?.net_price
                                      ?.code ?? ''
                                  }}</strong>
                                </td>
                              </tr>
                            </template>
                            <!-- grouped addons ends -->
                            <!-- logo technology addons starts -->
                            <template
                              v-for="(logo_technology, logo_technologies_index) in factory_product
                                .prices.logo_technologies"
                              :key="`logo_technology_${logo_technologies_index}_addon`"
                            >
                              <tr class="text-center">
                                <td class="p-1 w-[25%] text-left">
                                  {{ logo_technology.addon_name }}
                                </td>
                                <td class="p-1 w-[25%]"></td>
                                <td class="p-1 w-[25%]">
                                  <input
                                    :disabled="!can('update-price-breakdown')"
                                    type="number"
                                    class="w-20 p-1 mr-2 border border-gray-300 rounded-md outline-none"
                                    v-model="logo_technology.final_net_price"
                                    @change="
                                      () => {
                                        logo_technology.final_net_price =
                                          logo_technology.final_net_price
                                            ? parseFloat(logo_technology.final_net_price)
                                            : 0;
                                        handlePriceUpdate(
                                          logo_technology,
                                          factory_product.prices?.total_quantity
                                        );
                                      }
                                    "
                                  />{{
                                    order_ref.additional_fields?.currency?.net_price
                                      ?.code ?? ''
                                  }}
                                </td>
                                <td class="p-1 w-[25%] text-right">
                                  {{ formatNumber(logo_technology.final_net_price_with_quantity) }}
                                  <strong>{{
                                    order_ref.additional_fields?.currency?.net_price
                                      ?.code ?? ''
                                  }}</strong>
                                </td>
                              </tr>
                            </template>
                            <!-- logo technology addons ends -->
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <hr class="mt-1 border border-gray-300" />
                <table class="w-full">
                  <tbody>
                    <tr class="text-center">
                      <td class="p-1 w-[25%]"></td>
                      <td class="p-1 w-[25%]">
                        Qty:
                        <strong>{{ factory_product.prices?.total_quantity }}</strong>
                      </td>
                      <td class="p-1 w-[25%]"></td>
                      <td class="p-1 w-[25%] text-right">
                        <strong>Total:</strong>
                        {{ formatNumber(factory_product.prices.factory_product_total_price) }}
                        <strong>{{
                          order_ref.additional_fields?.currency?.net_price?.code ?? ''
                        }}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>
        </template>

        <hr />
        <table class="w-full">
          <tbody>
            <tr>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]" style="text-align: left; padding-left: 4.5%">
                Total Qty:
                <strong>{{ order_ref.price_info?.total_quantity }}</strong>
              </td>
              <td class="p-1 w-[25%] text-right">
                <strong>Total Price:</strong>
                {{ formatNumber(order_ref.price_info?.total_price ?? 0) }}
                <strong>{{
                  order_ref?.additional_fields?.currency?.net_price?.code ?? ''
                }}</strong>
              </td>
            </tr>
            <tr class="text-right">
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]">
                <strong>Shipping Cost:</strong>
                {{ formatNumber(order_ref.price_info?.shipping) }}
                <strong>{{
                  order_ref.additional_fields?.currency?.net_price?.code ?? ''
                }}</strong>
              </td>
            </tr>
            <tr class="text-right">
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>

              <td class="p-1 w-[25%]" v-if="order_ref.price_info?.is_fixed">
                <strong>Discount:</strong>
                {{
                  formatNumber(
                    order_ref.price_info?.fixed_value
                      ? order_ref.price_info?.fixed_value
                      : 0
                  )
                }}
                <strong>{{
                  order_ref.additional_fields?.currency?.net_price?.code ?? ''
                }}</strong>
              </td>
              <td class="p-1 w-[25%]" v-else>
                <strong>Discount:</strong> {{ formatNumber(discount_amount) }}
                <strong>{{
                  order_ref.additional_fields?.currency?.net_price?.code ?? ''
                }}</strong>
              </td>
            </tr>
            <tr class="text-right">
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]">
                <strong>Final Price:</strong>
                {{ formatNumber(order_ref.price_info?.order_final_price) }}
                <strong>{{
                  order_ref.additional_fields?.currency?.net_price?.code ?? ''
                }}</strong>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="flex gap-x-2 mt-5" v-if="can('update-price-breakdown')">
          <button
            class="flex gap-x-2 px-3 py-2 items-center text-[#219f84] border border-[#219f84] rounded-md cursor-pointer hover:text-white hover:bg-[#219F84] duration-200"
            @click="addProductModal = true"
          >
            <plus-icon class="size-5"></plus-icon> Add Item
          </button>
          <template v-if="can('update-techpack')">
            <a
              :href="`${appUrl}/edit-techpack/${order_ref.id}?source=new_dashboard`"
              class="border p-2 rounded-md border-[#219F84] text-[#219F84]"
            >
              Edit Tech Pack
            </a>
          </template>
        </div>
      </template>
      <template #footer>
      <div class="w-full flex flex-col">
       <div
          class="flex w-full items-end"
          :class="{
            'justify-between': can('update-price-breakdown'),
            'justify-end': !can('update-price-breakdown'),
          }"
        >
          <div class="flex gap-x-5 items-end" v-if="can('update-price-breakdown')">
            <button
              @click="shippingAndDiscountModal = true"
              class="border p-2 rounded-md border-[#219F84] text-[#219F84] h-max"
            >
              Shipping and discount
            </button>

            <div class="flex flex-col items-start gap-1">
              <label for="orderType" class="text-[14px]">Order Type:</label>
              <Select
                append-to-body
                v-model="selectedOrderType"
                :options="orderTypes"
                label="label"
                placeholder="Select Order Type"
                :clearable="false"
                @option:selected="handleOrderTypeUpdate"
                :class="{ 'border-red-500': errorMessages['order_type'] }"
                class="min-w-[300px] w-fit"
              />
            </div>
            <div class="flex flex-col items-start gap-1">
              <label for="orderType" class="text-[14px]">Invoice Memo:</label>
              <input
              class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
              type="text"
              v-model="invoice_memo"
              placeholder="Invoice Memo"
            />
            </div>
            <div class="flex flex-col items-start gap-1"  v-if="can('change-order-billing-merchant')">
              <label for="orderType" class="text-[14px]">Billing Merchant:</label>
               <Select
                  ref="billingSelect"
                  v-model="selectedBillingCompany"
                  :options="billingMerchant"
                  @update:modelValue="handleBillingMerchantUpdate"
                  label="company_name"
                  :reduce="merchant => merchant.id"
                  :clearable="false"
                  class="min-w-[300px] w-fit"
                  :disabled="order_ref.stripe_invoice_id"
                />
            </div>
          </div>


          <div class="flex gap-x-5">
            <button
              class="border p-2 rounded-md bg-[#219F84] text-white h-max"
              @click="updateOrderPrices(false)"
            >
              Save
            </button>
          </div>
        </div>
      <div class="w-full mt-4  max-w-[50%]">
        <textarea
          v-model="internal_note"
          rows="2"
          class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
          placeholder="Internal Note"
        ></textarea>
      </div>
      </div>

      </template>
    </Dialog>
  </Transition>
  <Transition name="fade">
    <AddProductModal
      v-if="addProductModal"
      @on-close="() => (addProductModal = false)"
      :customerId="(order_item as DashboardItem).order.customer_id"
      :companyId="(order_item as DashboardItem).order.company_id"
      :orderItem="order_item"
      :lockerRoom="lockerRoom"
      :factories="factories"
      :company_products="company_products"
      :copiedRoster="copiedRoster"
      @copy-roster="copyRoster"
      @add-factory-product="addFactoryProduct"
    />
  </Transition>
  <Transition name="fade">
    <Dialog
      v-if="shippingAndDiscountModal"
      title="Add Shipping and Discount"
      :showCloseButton="true"
      :showFooter="true"
      @close-event="discount_shipping_info = JSON.parse(JSON.stringify(order_ref.price_info))"
      @cancel-event="discount_shipping_info = JSON.parse(JSON.stringify(order_ref.price_info))"
      :showSaveButton="false"
      :showCancelButton="false"
      :custom-footer="true"
      :sm="true"
      @closeEvent="closeShippingAndDiscountModal"
    >
      <template #modal-content>
        <div>
          <div class="flex justify-between">
            <label class="block mb-1 text-sm font-medium text-gray-700">Add Discount</label>
            <div class="flex items-center gap-x-2">
              <input
                type="checkbox"
                name="Fixed Discount"
                class="w-4 h-4"
                tr
                v-model="discount_shipping_info.is_fixed"
              />
              <label for="Fixed Discount">Fixed Price</label>
            </div>
          </div>
          <template v-if="discount_shipping_info.is_fixed">
            <input
              class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
              type="number"
              v-model="discount_shipping_info.fixed_value"
              @change="
                () => {
                  discount_shipping_info.fixed_value = discount_shipping_info.fixed_value
                    ? formatNumber(discount_shipping_info.fixed_value)
                    : 0;
                }
              "
              placeholder="Enter Fixed Price"
            />
          </template>
          <template v-else>
            <div class="relative">
              <input
                class="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md outline-none"
                type="number"
                v-model="discount_shipping_info.discount"
                @change="
                  () => {
                    discount_shipping_info.discount = discount_shipping_info.discount
                      ? formatNumber(discount_shipping_info.discount)
                      : 0;
                  }
                "
                placeholder="Enter Discount Percentage"
              />
              <span class="absolute transform -translate-y-1/2 right-3 top-1/2">%</span>
            </div>
          </template>
          <label class="block mt-4 mb-1 text-sm font-medium text-gray-700">Add Shipping</label>
          <input
            class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
            type="number"
            v-model="discount_shipping_info.shipping"
            @change="
              () => {
                discount_shipping_info.shipping = discount_shipping_info.shipping
                  ? formatNumber(discount_shipping_info.shipping)
                  : 0;
              }
            "
            placeholder="Enter Shipping Cost"
          />
          <span class="absolute transform -translate-y-1/2 right-6" style="top: 65%">{{
            order_ref.additional_fields?.currency?.net_price?.code ?? ''
          }}</span>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end w-full">
          <button
            @click="calculateShippingAndDiscount"
            class="border px-4 py-2 rounded-md border-[#219F84] text-[#219F84]"
          >
            Save
          </button>
        </div>
      </template>
    </Dialog>
  </Transition>
</template>
<script setup lang="ts">
import Dialog from '@/components/Shared/Dialog.vue';
import Dropdown from '@/components/Shared/Dropdown.vue';
import Select from '@/components/Shared/Select.vue';
import { can } from '@/composables';
import { showConfirm, showToast } from '@/composables/swal';
import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import { ApiResponse2, DashboardItem, OrderType, Product, Result } from '@/models';
import { useMainStore } from '@/store';
import { orderTypes } from '@/utils/constants/orderTypes';
import { PlusIcon, TrashIcon } from '@heroicons/vue/24/outline';
import { AxiosError } from 'axios';
import { chunk } from 'lodash';
import { BadgeCheck, ChevronDownIcon, CircleX, Clock3, FileText, Trash2, TriangleAlert, SquareM } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, onMounted, reactive, ref,watch } from 'vue';
import AddProductModal from './AddProductModal.vue';
import { CopiedRoster } from '@/models/CopiedRoster';
import Swal from "sweetalert2";
const { loading } = storeToRefs(useMainStore());
const stripeLoading = ref(false);

const stripeDashboardUrl = import.meta.env.VITE_APP_STRIPE_DASHBOARD_URL;

const props = defineProps<{
  order_item: any;
  order: any;
  appUrl: string;
}>();

const copiedRoster = ref<CopiedRoster>({
  isAnyRosterCopied: false,
  copiedFromProductIndex: null,
  rosterData: []
});

const order_ref = ref(JSON.parse(JSON.stringify(props.order)))
const order_items = order_ref.value.items;



const billingMerchant=ref([]);
const selectedBillingCompany = ref(props.order?.billing_company_id || props.order?.company_id);
const previousBillingCompany = ref(null);
const billingSelect = ref(null);
const internal_note = ref(order_ref.value.internal_note || '');

watch(selectedBillingCompany, (newVal, oldVal) => {
  previousBillingCompany.value = oldVal;
});

function handleBillingMerchantUpdate() {

  if(order_ref.value.stripe_invoice_id) {
    showToast("Cannot change billing merchant for orders with existing invoices.","error");
    selectedBillingCompany.value = previousBillingCompany.value;
    return;
  }
  Swal.fire({
    title: "Are you sure?",
    text: "Changing the billing merchant will update the order price accordingly. Do you want to continue?.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes, continue",
    cancelButtonText: "No"
  }).then((result) => {
    if (result.isConfirmed) {
       billingSelect.value?.$el?.blur();

      http.post(`update/order/${order_ref.value.id}/billing_merchant`, {
        billing_company_id: selectedBillingCompany.value,
        billing_company_name:
          billingMerchant.value.find(
            (c) => c.id === selectedBillingCompany.value
          )?.company_name || null
      }).then(successResponse => {
        showToast(successResponse.data.message);
        emit("handleOrderUpdated", true);
      });
    } else {
      selectedBillingCompany.value = previousBillingCompany.value;
      billingSelect.value?.$el?.blur();

    }
  });
}

const editingInvoiceNo = ref(false);
const invoiceNoInput = ref(''); // Used for both add and edit
const savingInvoice = ref(false);

const saveInvoiceNumber = async () => {
  if (!invoiceNoInput.value || !invoiceNoInput.value.toString().trim()) return;
  savingInvoice.value = true;
  try {
    const payload = { stripe_invoice_number: invoiceNoInput.value.toString().trim() };
    const response = await http.put(`order/stripe-invoice-no/${order_ref.value.id}`, payload);
    const resData = response.data;
    const returnedInvoiceNo = resData?.result?.stripe_invoice_number ?? invoiceNoInput.value;
    order_ref.value.stripe_invoice_number = returnedInvoiceNo;
    editingInvoiceNo.value = false;
    invoiceNoInput.value = '';
    showToast(resData?.message ?? 'Invoice number updated', 'success');
  } catch (err) {
    handleResponseException(err);
  } finally {
    savingInvoice.value = false;
  }
};

const startEditingInvoiceNo = () => {
  editingInvoiceNo.value = true;
  invoiceNoInput.value = order_ref.value.stripe_invoice_number;
}




const addFactoryProduct = (payload: any) => {
  const factoryId = payload.factory_id;
  let matchedItem = null;

  matchedItem = order_ref.value.items.find(
    (item: any) => item.factory_id === factoryId
  );

  if (matchedItem) {
    matchedItem.factory_products.push(payload.factory_products[0]);
  } else {
    order_ref.value.items.push({
      ...payload,
      id: null,
      status: factoryId ? "order_approve" : "submitted_for_factory_review",
      order_id: order_ref.value.id,
      order: {
        id: order_ref.value.id,
        order_no: order_ref.value.order_no,
        order_type_detail: order_ref.value.order_type_detail,
        order_type_symbol: order_ref.value.order_type_symbol,
        order_type_default: order_ref.value.order_type_default,
        company_id: order_ref.value.company_id,
        customer_id: order_ref.value.customer_id,
      },
    });
  }
  calculateTotalPrice();
};

const paymentStatuses = [
  {
    status: "draft",
    description: "Newly created, not finalized",
    icon: FileText,
    color: "bg-gray-100 text-gray-600"
  },
  {
    status: "open",
    description: "Finalized, awaits payment",
    icon: Clock3,
    color: "bg-blue-100 text-blue-600"
  },
  {
    status: "paid",
    description: "Fully paid",
    icon: BadgeCheck,
    color: "bg-green-100 text-green-600"
  },
  {
    status: "void",
    description: "Voided, no payment required",
    icon: Trash2,
    color: "bg-gray-100 text-gray-600"
  },
  {
    status: "uncollectable",
    description: "Marked as uncollectable",
    icon: CircleX,
    color: "bg-red-100 text-red-600"
  },
  {
    status: "overdue",
    description: "Past due date, unpaid",
    icon: TriangleAlert,
    color: "bg-orange-100 text-orange-500"
  },
  {
    status: "manual",
    description: "Manual invoice",
    icon: SquareM,
    color: "bg-yellow-100 text-orange-500"
  }
]

const statusObjects = computed(() =>
  paymentStatuses.filter(
    s => s.status === order_ref.value?.stripe_invoice_status
  )
)

const selectedOrderType = ref(null);

function handleOrderTypeUpdate(order_type: OrderType) {
  selectedOrderType.value = order_type;
}
const errorMessages = reactive<Record<string, string>>({});

//order discount and shipping starts
const discount_shipping_info = ref<any>({
  is_fixed: false,
  fixed_value: 0.0,
  discount: 0.0,
  shipping: 0.0,
  total_price: 0,
  total_quantity: 0,
  order_final_price: 0,
});

const discount_amount = computed(() => {
  const discount = order_ref.value.price_info?.discount
    ? parseFloat(order_ref.value.price_info?.discount)
    : 0;
  return (order_ref.value.price_info?.total_price * discount) / 100;
});
//order discount and shipping ends

const emit = defineEmits(['close','handleOrderUpdated']);
const priceBreakdownModal = ref<boolean>(false);
const shippingAndDiscountModal = ref<boolean>(false);
const addProductModal = ref<boolean>(false);

let company_products = ref<Product[]>([]);
const getMerchantProducts = async (company_id: number) => {
  http
    .get<ApiResponse2<Result<Product[]>>>(`merchant_products?company_id=${company_id}`)
    .then((successResponse: any) => {
      company_products.value = successResponse.data.result.products;

    });
};
const { getCustomerLockerRoom ,getFactories, merchantUsers} = useMainStore();
const { lockerRoom, factories  } = storeToRefs(useMainStore());
onMounted(async () => {
  priceBreakdownModal.value = true;
  discount_shipping_info.value = order_ref.value?.price_info;
  selectedOrderType.value = {
    label: order_ref.value.order_type_detail || '',
    symbol: order_ref.value.order_type_symbol || '',
    default: order_ref.value.order_type_default || false,
  };
  getMerchantProducts(order_ref.value.company_id);
  getCustomerLockerRoom(order_ref.value.company_id, order_ref.value.customer_id);
  getFactories()
  billingMerchant.value = merchantUsers;
});

const closeEvent = async () => {
  emit('close', false);
  priceBreakdownModal.value = false;
};

const closeShippingAndDiscountModal = () => {
  shippingAndDiscountModal.value = false;
};

const storage_url = import.meta.env.VITE_APP_FILE_URL;

const handlePriceUpdate = (item: any, quantity: any) => {
  item.final_net_price = formatNumber(item.final_net_price);
  const total = quantity * parseFloat(item.final_net_price);
  item.final_net_price_with_quantity = formatNumber(total);
  calculateTotalPrice();
};

const getFilePreview = (factory_product: any) => {
  if (factory_product.front_image_file instanceof File) {
    return factory_product.front_image_file
      ? URL.createObjectURL(factory_product.front_image_file)
      : '';
  } else {
    return `${storage_url}/${factory_product.front_image}`;
  }
};

const formatNumber = (value) => {
  const num = parseFloat(value) || 0;

  let str = num.toFixed(3); // always start with 3 decimals
  const [intPart, decPart] = str.split('.');

  // all decimals are zero → return integer
  if (decPart === '000') {
    return intPart;
  }

  // third decimal is zero → keep first two
  if (decPart[2] === '0') {
    return `${intPart}.${decPart.slice(0, 2)}`;
  }

  // all three decimals are meaningful → keep all
  return `${intPart}.${decPart}`;
};

const calculateTotalPrice = () => {
  let orderTotalPrice = 0;
  let orderTotalQuantity = 0;
  order_items.forEach(orderItem => {
    let order_item_total_price = 0;
    let order_item_total_quantity = 0;
    orderItem.factory_products.forEach(factory_product => {
      order_item_total_quantity =
      order_item_total_quantity + parseInt(factory_product.prices?.total_quantity ?? 0);
    let total = 0;
    factory_product.prices.sizes.forEach((size: any) => {
      total += parseFloat(size.final_net_price_with_quantity);
    });
    factory_product.prices.addons.forEach((addon: any) => {
      total += parseFloat(addon.final_net_price_with_quantity);
    });
    factory_product.prices.ungrouped_addons.forEach((ungrouped_addon: any) => {
      total += parseFloat(ungrouped_addon.final_net_price_with_quantity);
    });
    Object.values(factory_product.prices.grouped_addons).forEach((grouped_addon: any) => {
      total += parseFloat(grouped_addon.final_net_price_with_quantity);
    });
    factory_product.prices.logo_technologies.forEach((logo_technology: any) => {
      total += parseFloat(logo_technology.final_net_price_with_quantity);
    });
    factory_product.prices.factory_product_total_price = total;
    order_item_total_price = order_item_total_price + total;
    });
    if (!orderItem.price_info) {
      orderItem.price_info = {
        total_price: 0,
        total_quantity: 0,
        order_final_price: 0,
      };
    }

    orderItem.price_info.total_price = order_item_total_price;
    orderTotalPrice += order_item_total_price;
    orderItem.price_info.total_quantity = order_item_total_quantity;
    orderTotalQuantity += order_item_total_quantity
    //order_item_final_price property is no more in use in future if we apply dicount on order item then it can be used. For now it is moved to order price info as order_final _price
    //orderItem.price_info.order_item_final_price = order_item_total_price;
  })
  if (!order_ref.value.price_info) {
    order_ref.value.price_info = {
      total_price: 0,
      total_final_price: 0,
      total_quantity: 0,
    };
  }

  order_ref.value.price_info.total_price = orderTotalPrice;
  order_ref.value.price_info.total_final_price = orderTotalPrice;
  order_ref.value.price_info.total_quantity = orderTotalQuantity;
  calculateShippingAndDiscount();
};

const calculateShippingAndDiscount = () => {
  const order_item_total_price = parseFloat(order_ref.value.price_info?.total_price ?? 0);
  let order_final_price = parseFloat(order_ref.value.price_info.order_final_price);
  discount_shipping_info.value.fixed_value = discount_shipping_info.value.fixed_value
    ? parseFloat(discount_shipping_info.value.fixed_value)
    : 0;
  discount_shipping_info.value.discount = discount_shipping_info.value.discount
    ? parseFloat(discount_shipping_info.value.discount)
    : 0;
  discount_shipping_info.value.shipping = discount_shipping_info.value.shipping
    ? parseFloat(discount_shipping_info.value.shipping)
    : 0;
  order_ref.value.price_info.is_fixed = discount_shipping_info.value.is_fixed;
  order_ref.value.price_info.fixed_value = discount_shipping_info.value.fixed_value;
  order_ref.value.price_info.discount = discount_shipping_info.value.discount;
  order_ref.value.price_info.shipping = discount_shipping_info.value.shipping;
  if (discount_shipping_info.value.is_fixed) {
    order_final_price =
      order_item_total_price -
      (discount_shipping_info.value.fixed_value ? discount_shipping_info.value.fixed_value : 0);
  } else {
    const discount = discount_shipping_info.value.discount
      ? discount_shipping_info.value.discount
      : 0;
    const discount_amount = (order_item_total_price * discount) / 100;
    order_final_price = order_item_total_price - discount_amount;
  }
  const shipping = discount_shipping_info.value.shipping
    ? discount_shipping_info.value.shipping
    : 0;
  order_final_price = order_final_price + shipping;
  order_ref.value.price_info.order_final_price = formatNumber(order_final_price);
  closeShippingAndDiscountModal();
};
const uploadFactoryProductsFiles = async (order_items: any[]) => {
  const base_path = `comapny_${order_ref.value.company_id}/${order_ref.value.company_id}/order/${order_ref.value.customer_id}/${order_ref.value.id}`;
  const allPromises: Promise<any>[] = [];

  for (const [orderItemIndex, orderItem] of order_items.entries()) {
    const file_base_path = orderItem.factory_id ? `${base_path}/${orderItem.factory_id}` : `${base_path}/shareable`;

    for (const [factoryProductIndex, factoryProduct] of orderItem.factory_products.entries()) {
      const formData = new FormData();

      if (factoryProduct.front_image_file instanceof File) formData.append('front_image', factoryProduct.front_image_file);
      if (factoryProduct.back_image_file instanceof File) formData.append('back_image', factoryProduct.back_image_file);

      if (Array.isArray(factoryProduct.assets)) {
        factoryProduct.assets.forEach(asset => {
          if (asset instanceof File) formData.append('assets[]', asset);
        });
      }

      const hasFiles = formData.has('front_image') || formData.has('back_image') || formData.has('assets[]');
      if (!hasFiles) continue;

      formData.append('base_path', file_base_path);

      allPromises.push(
        http.post('upload/price/modal/files', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',}})
          .then(res => ({ orderItemIndex, factoryProductIndex, data: res.data.result }))

      );
    }
  }

  const factoryProductsFilesData = await Promise.allSettled(allPromises);
  const filesUploadfailedResponses = factoryProductsFilesData.filter((r): r is PromiseRejectedResult => r.status === "rejected");
  const filesUploadSuccessResponses = factoryProductsFilesData.filter((r): r is PromiseFulfilledResult<any> => r.status === "fulfilled");
    if(filesUploadfailedResponses.length > 0) {
      filesUploadfailedResponses.forEach(filesUploadfailedResponse => {
        handleResponseException((filesUploadfailedResponse.reason as AxiosError));
      })
    }
  filesUploadSuccessResponses.forEach((factoryProductsFilesDatum: any) => {
    const status = factoryProductsFilesDatum.status;
    if(status === "fulfilled") {
      const {
      value: {
        orderItemIndex,
        factoryProductIndex,
        data: {
          front_image,
          back_image,
          assets
        }
      }
    } = factoryProductsFilesDatum;
    order_items[orderItemIndex]['factory_products'][factoryProductIndex].front_image = front_image
    order_items[orderItemIndex]['factory_products'][factoryProductIndex].back_image = back_image
    order_items[orderItemIndex]['factory_products'][factoryProductIndex].assets = assets
    delete order_items[orderItemIndex]['factory_products'][factoryProductIndex].front_image_file;
    delete order_items[orderItemIndex]['factory_products'][factoryProductIndex].back_image_file;
    }
  });
};

const uploadFactoryProductsFiles1 = async (order_items: any[]) => {
  let base_path = `comapny_${order_ref.value.company_id}/${order_ref.value.company_id}/order/${order_ref.value.customer_id}/${order_ref.value.id}`;
  for (const order_item of order_items) {
    let file_path = '';
    if (order_item.factory_id) {
      file_path = `${base_path}/${order_item.factory_id}`;
    } else {
      file_path = `${base_path}/shareable`;
    }
    const factory_product_files = order_item.factory_products
      .map(async (factory_product: any, factory_product_index: number) => {
        const factory_product_form_data = new FormData();

        if (factory_product.front_image_file instanceof File) {
          factory_product_form_data.append('front_image', factory_product.front_image_file);
        }
        if (factory_product.back_image_file instanceof File) {
          factory_product_form_data.append('back_image', factory_product.back_image_file);
        }
        if (Array.isArray(factory_product.assets)) {
          factory_product.assets.forEach((asset, asset_index) => {
            if (asset instanceof File) {
              factory_product_form_data.append(`assets[${asset_index}]`, asset);
            }
          });
        }

        if (
          factory_product_form_data.has('front_image') ||
          factory_product_form_data.has('back_image') ||
          factory_product_form_data.has('assets[0]')
        ) {
          factory_product_form_data.append('base_path', base_path);

          const factory_product_response = await http.post(
            'upload/price/modal/files',
            factory_product_form_data,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );

          return {
            status: factory_product_response.status,
            factory_product_index,
            data: factory_product_response.data.result,
            order_item,
          };
        } else {
          return null;
        }
      })
      .filter(Boolean);

    const results = await Promise.allSettled(factory_product_files);

    results.forEach((result: any) => {
      if (result.status === 'fulfilled' && result.value) {
        const { factory_product_index, data } = result.value;

        order_item.factory_products[factory_product_index].front_image = data.front_image;
        order_item.factory_products[factory_product_index].back_image = data.back_image;
        order_item.factory_products[factory_product_index].assets = data.assets;

        delete order_item.factory_products[factory_product_index].front_image_file;
        delete order_item.factory_products[factory_product_index].back_image_file;
      }
    });
  }
};
const updateOrderPrices = async (createInvoice: boolean = false) => {
  loading.value = true;
  //create order items chunks
  const order_items_chunks = chunk(order_items, 10);
  const allPromises: Promise<any>[] = [];
    //loop through order items chunks
    for (const orderItemsChunk of order_items_chunks) {
      //upload order items chunk all files and add order items chunk all factory products front, backimage and assets path
      await uploadFactoryProductsFiles(orderItemsChunk);
      const orderItemsData = [];
      for (const  orderItem of orderItemsChunk) {

        const typedOrderItem: any = orderItem
        orderItemsData.push({
          id: typedOrderItem.id,
          factory_id: typedOrderItem.factory_id,
          factory_name: typedOrderItem.factory_name,
          price_info: typedOrderItem.price_info,
          factory_products: typedOrderItem.factory_products,
          status: typedOrderItem.status,

        })
      }
      allPromises.push(
        http.post(`update/order/${order_ref.value.id}/order_items/price`, {order_items: orderItemsData})
      );
  }
  Promise.allSettled(allPromises).then(results => {
    const failed = results.filter((r): r is PromiseRejectedResult => r.status === "rejected");
    failed.forEach(failedItem => {
      handleResponseException((failedItem.reason as AxiosError));
    })
    let selectedOrderTypeLabel = '';
    let selectedOrderTypeSymbol = '';

    if(selectedOrderType.value && (selectedOrderType as Record<any, any>).value.label) {
      selectedOrderTypeLabel = (selectedOrderType as Record<any, any>).value.label
    }
    if(selectedOrderType.value && (selectedOrderType as Record<any, any>).value.symbol) {
      selectedOrderTypeSymbol = (selectedOrderType as Record<any, any>).value.symbol
    }
    http.post(`update/order/${order_ref.value.id}/order_items/price`, {
      update_order_only: true,
      selected_order_type: selectedOrderTypeLabel,
      selected_order_type_symbol: selectedOrderTypeSymbol,
      price_info: order_ref.value.price_info,
      invoice_memo: invoice_memo.value,
      internal_note: internal_note.value
    }).then(successResponse => {
      if(createInvoice) {
        handleStripeSync()
      } else {
        loading.value = false;
        emit('close', true);
        priceBreakdownModal.value = false;
        showToast(successResponse.data.message);
      }
    })
  });
};
const invoice_memo = ref(order_ref.value.stripe_invoice_info?.invoice_comment || '');
const handleStripeSync = async () => {
  stripeLoading.value = true;
  const response = http
    .post(`customer-orders/${order_ref.value.id}/sync-to-stripe`, {'invoice_memo': invoice_memo.value})
    .then((response: any) => {
      const responseData = response.data;
      const {
        message,
        result: { invoice },
      } = responseData;
      emit('close', true);
      priceBreakdownModal.value = false;
      stripeLoading.value = false;
      loading.value = false;
      showToast(message);
    })
    .catch((error) => {
      console.log('error', error);
      stripeLoading.value = false;
      loading.value = false;
      handleResponseException(error);
    });
};

const updateInvoiceState = async (invoice) => {
  if (invoice) {
    order_ref.value.stripe_invoice_id = invoice.invoiceId;
    order_ref.value.stripe_invoice_info = {
      hosted_invoice_url: invoice.hosted_invoice_url,
      invoice_pdf: invoice.invoice_pdf,
    };
  }
};

const removeFactoryProduct = (orderItemIndex, factoryProductIndex) => {
  order_items[orderItemIndex]['factory_products'].splice(factoryProductIndex, 1)
  calculateTotalPrice();

}

const copyRoster = (copiedRosterData: any) => {
  if(copiedRoster.value.isAnyRosterCopied) {
    showConfirm(
      'Overwrite existing copied roster?',
      'You already have a roster copied. Do you want to copy this product’s roster instead?',
    ).then((result) => {
      if (result) {
        setCopiedRoster(copiedRosterData.product_id, copiedRosterData.rosterData);
        showToast('Roster copied successfully', 'success');
      }
    });
  } else {
    setCopiedRoster(copiedRosterData.product_id, copiedRosterData.rosterData);
    showToast('Roster copied successfully', 'success');
  }
};

const setCopiedRoster = (factory_product_index: number, roster: any[]) => {
  copiedRoster.value.isAnyRosterCopied = true;
  copiedRoster.value.copiedFromProductIndex = factory_product_index;
  copiedRoster.value.rosterData = roster;
};
</script>

<style lang="scss">
.products-table {
  th,
  td {
    border: 1px solid #e5e7eb;
    padding: 5px;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: white;
  }

  th {
    position: sticky;
    top: 0;
    background-color: white;
    z-index: 10;
  }
}

.modal-body {
  padding-top: 0 !important;
}
</style>
